import { AppBar, Box, Button, Card, CardActions, CardContent, CardHeader, Chip, Dialog, Unstable_Grid2 as Grid, IconButton, Stack, TextField, Toolbar, Typography } from "@mui/material";
import { MinusCircle, PlusCircle } from "@untitled-ui/icons-react/build/esm";
import { ObjectID } from "bson";
import { pick } from "lodash";
import { AdditionalFeeInput, FileInputLike, FileProductInputLike, FileProductOptionInputLike, FixDiscountInput } from "~served/utils/calculations/types";
import { useTillSelectedProduct } from "~/hooks/use-till-selected-item";
import { useGetProducts } from "~/queries/useGetProducts";
import { GetTillProductCategoriesResults } from "~/queries/useGetTillProductCategories";
import { useSetTillIsEditingFileInput, useTill, useUpdateTillCreateFileInput, useUpdateTillTempProductInput, useUpdateTillUpdateFileInput } from "~/store";
import { CreateFileInput, DISCOUNT_TYPE, FileProductOptionInput } from "~/types/__generated/gql/graphql";
import { applyAdditionalFeeToProduct, applyFixDiscountToProduct, bigMath, calculateFile } from "~served/utils";
import { useEffect, useState } from "react";
import { FileProductOptionMultiCurrencyConfig } from "~served/types/db";
import { $dTillOptionPrice } from "~/utils/multi-currency";
export const TempProductModal = () => {
  const {
    data: products = []
  } = useGetProducts();
  const {
    tempProductInput,
    createFileInput,
    updateFileInput
  } = useTill();
  const updateTempProduct = useUpdateTillTempProductInput();
  const updateCreateFileInput = useUpdateTillCreateFileInput();
  const updateUpdateFileInput = useUpdateTillUpdateFileInput();
  const setTillIsEditFileInput = useSetTillIsEditingFileInput();
  const selectedProduct = useTillSelectedProduct();
  const [tmpDiscount, setTmpDiscount] = useState<FixDiscountInput>({
    type: DISCOUNT_TYPE.PERCENTAGE,
    value: 0
  });
  const [tmpFee, setTmpFee] = useState<AdditionalFeeInput>({
    type: DISCOUNT_TYPE.PERCENTAGE,
    value: 0
  });
  useEffect(() => {
    if (tempProductInput) {
      const cartFile = updateFileInput ? updateFileInput : createFileInput;
      const {
        $isAllProductsWithFixDiscount,
        $isAllProductsWithAdditionalFee,
        products
      } = calculateFile(cartFile as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>);
      const discount = $isAllProductsWithFixDiscount ? {
        ...(products[0].subtotalAddons.fixDiscount.fixDiscount ? {
          fixDiscount: products[0].subtotalAddons.fixDiscount.fixDiscount
        } : undefined),
        type: products[0].subtotalAddons.fixDiscount.type,
        value: products[0].subtotalAddons.fixDiscount.value
      } as unknown as FixDiscountInput : {
        type: DISCOUNT_TYPE.PERCENTAGE,
        value: 0
      };
      const fee = $isAllProductsWithAdditionalFee ? {
        ...(products[0].subtotalAddons.additionalFee.additionalFee ? {
          additionalFee: products[0].subtotalAddons.additionalFee.additionalFee
        } : undefined),
        type: products[0].subtotalAddons.additionalFee.type,
        value: products[0].subtotalAddons.additionalFee.value
      } as unknown as AdditionalFeeInput : {
        type: DISCOUNT_TYPE.PERCENTAGE,
        value: 0
      };
      setTmpDiscount(discount);
      setTmpFee(fee);
    }
  }, [createFileInput, tempProductInput, updateFileInput]);
  const handleClose = () => {
    if (createFileInput && tempProductInput) {
      if (tempProductInput.quantity === 0) {
        updateCreateFileInput({
          products: createFileInput.products.filter(p => p._id !== tempProductInput._id)
        });
      } else {
        const isAlreadyAdded = createFileInput.products.some(p => p._id === tempProductInput._id);
        if (isAlreadyAdded) {
          updateCreateFileInput(calculateFile({
            ...createFileInput,
            products: [...createFileInput.products.map(p => p._id === tempProductInput._id ? applyAdditionalFeeToProduct({
              product: applyFixDiscountToProduct({
                product: tempProductInput as unknown as FileProductInputLike,
                discount: tmpDiscount
              }),
              fee: tmpFee
            }) : p)]
          } as unknown as FileInputLike).$file as unknown as CreateFileInput);
        } else {
          updateCreateFileInput(calculateFile({
            ...createFileInput,
            products: [...createFileInput.products, applyAdditionalFeeToProduct({
              product: applyFixDiscountToProduct({
                product: tempProductInput as unknown as FileProductInputLike,
                discount: tmpDiscount
              }),
              fee: tmpFee
            })]
          } as unknown as FileInputLike).$file as unknown as CreateFileInput);
        }
      }
    }
    if (updateFileInput && tempProductInput) {
      if (tempProductInput.quantity === 0) {
        updateUpdateFileInput({
          products: updateFileInput.products.filter(p => p._id !== tempProductInput._id)
        });
      } else {
        const isAlreadyAdded = updateFileInput.products.some(p => p._id === tempProductInput._id);
        if (isAlreadyAdded) {
          updateUpdateFileInput(calculateFile({
            ...updateFileInput,
            products: [...updateFileInput.products.map(p => p._id === tempProductInput._id ? applyAdditionalFeeToProduct({
              product: applyFixDiscountToProduct({
                product: tempProductInput as unknown as FileProductInputLike,
                discount: tmpDiscount
              }),
              fee: tmpFee
            }) : p)]
          } as unknown as FileInputLike).$file as unknown as CreateFileInput);
        } else {
          updateUpdateFileInput(calculateFile({
            ...updateFileInput,
            products: [...updateFileInput.products, applyAdditionalFeeToProduct({
              product: applyFixDiscountToProduct({
                product: tempProductInput as unknown as FileProductInputLike,
                discount: tmpDiscount
              }),
              fee: tmpFee
            })]
          } as unknown as FileInputLike).$file as unknown as CreateFileInput);
        }
      }
    }
    updateTempProduct(null);
    setTillIsEditFileInput(true);
  };
  const getOptionMultiCurrencyConfig = (option: GetTillProductCategoriesResults[number]["_products"][number]["_options"][number]) => {
    if (createFileInput) {
      return createFileInput.multiCurrencyConfig ? {
        multiCurrencyConfig: {
          selectedCurrency: createFileInput.multiCurrencyConfig.selectedCurrency,
          priceConfigs: option.priceConfigs ? option.priceConfigs : createFileInput.multiCurrencyConfig.currencyConfigs.map(c => ({
            ...c,
            price: bigMath.mul(option.listedPrice, c.rate)
          }))
        } as FileProductOptionMultiCurrencyConfig
      } : undefined;
    }
    if (updateFileInput) {
      return updateFileInput.multiCurrencyConfig ? {
        multiCurrencyConfig: {
          selectedCurrency: updateFileInput.multiCurrencyConfig.selectedCurrency,
          priceConfigs: option.priceConfigs ? option.priceConfigs : updateFileInput.multiCurrencyConfig.currencyConfigs.map(c => ({
            ...c,
            price: bigMath.mul(option.listedPrice, c.rate)
          }))
        } as FileProductOptionMultiCurrencyConfig
      } : undefined;
    }
    return undefined;
  };
  const getOptionPrice = (option: GetTillProductCategoriesResults[number]["_products"][number]["_options"][number]) => {
    if (createFileInput) {
      const config = createFileInput?.multiCurrencyConfig;
      return $dTillOptionPrice(option, config);
    }
    if (updateFileInput) {
      const config = updateFileInput?.multiCurrencyConfig;
      return $dTillOptionPrice(option, config);
    }
    return $dTillOptionPrice(option, undefined);
  };
  const onOptionClick = (option: GetTillProductCategoriesResults[number]["_products"][number]["_options"][number]) => {
    const isAlreadyAdded = tempProductInput!.options.some(o => o.option === option._id);
    if (isAlreadyAdded) {
      const newOptions = tempProductInput!.options.map(o => o.option === option._id ? {
        ...o,
        quantity: o.quantity + 1
      } : o);
      updateTempProduct({
        options: newOptions
      });
    } else {
      const newOption: FileProductOptionInputLike = {
        ...pick(option, ["listedPrice", "originalPrice", "originalPriceAddons"]),
        _id: new ObjectID().toString(),
        option: option._id,
        quantity: 1,
        ...getOptionMultiCurrencyConfig(option)
      };
      updateTempProduct({
        options: [...tempProductInput!.options, newOption as FileProductOptionInput]
      });
    }
  };
  const onIncreaseOptionQuantity = (optionId: string) => {
    const newOptions = tempProductInput!.options.map(o => o._id === optionId ? {
      ...o,
      quantity: o.quantity + 1
    } : o);
    updateTempProduct({
      options: newOptions
    });
  };
  const onDecreaseOptionQuantity = (optionId: string) => {
    const newOptions = tempProductInput!.options.map(o => o._id === optionId ? {
      ...o,
      quantity: o.quantity - 1
    } : o).filter(o => o.quantity > 0);
    updateTempProduct({
      options: newOptions
    });
  };
  const onDecreaseItemQuantity = () => {
    updateTempProduct({
      quantity: tempProductInput!.quantity - 1
    });
  };
  const onIncreaseItemQuantity = () => {
    updateTempProduct({
      quantity: tempProductInput!.quantity + 1
    });
  };
  const handleFeeTypeChanged = () => {
    const newType = tmpFee.type === DISCOUNT_TYPE.AMOUNT ? DISCOUNT_TYPE.PERCENTAGE : DISCOUNT_TYPE.AMOUNT;
    setTmpFee(o => ({
      ...o,
      type: newType
    }));
  };
  const handleFeeValueChanged = value => {
    const newValue = isNaN(Number(value)) || Number(value) < 0 ? 0 : Number(value);
    setTmpFee(o => ({
      ...o,
      value: newValue
    }));
  };
  const handleDiscountTypeChanged = () => {
    const newType = tmpDiscount.type === DISCOUNT_TYPE.AMOUNT ? DISCOUNT_TYPE.PERCENTAGE : DISCOUNT_TYPE.AMOUNT;
    setTmpDiscount(o => ({
      ...o,
      type: newType
    }));
  };
  const handleDiscountValueChanged = value => {
    const newValue = isNaN(Number(value)) || Number(value) < 0 ? 0 : Number(value);
    setTmpDiscount(o => ({
      ...o,
      value: newValue
    }));
  };
  return <Dialog fullWidth maxWidth="md" scroll="paper" open={!!tempProductInput} data-sentry-element="Dialog" data-sentry-component="TempProductModal" data-sentry-source-file="temp-product-modal.tsx">
			<Box sx={{
      height: "100dvh",
      overflow: "hidden"
    }} data-sentry-element="Box" data-sentry-source-file="temp-product-modal.tsx">
				<AppBar color="inherit" sx={{
        position: "relative"
      }} data-sentry-element="AppBar" data-sentry-source-file="temp-product-modal.tsx">
					<Toolbar data-sentry-element="Toolbar" data-sentry-source-file="temp-product-modal.tsx">
						<Typography sx={{
            ml: 2,
            flex: 1
          }} variant="h6" component="div" data-sentry-element="Typography" data-sentry-source-file="temp-product-modal.tsx">
							{products.find(p => p._id === tempProductInput?.product)?.name}
						</Typography>
						<Stack direction="row" alignItems="center" data-sentry-element="Stack" data-sentry-source-file="temp-product-modal.tsx">
							<IconButton color="inherit" disabled={tempProductInput?.quantity === 0} onClick={onDecreaseItemQuantity} data-sentry-element="IconButton" data-sentry-source-file="temp-product-modal.tsx">
								<MinusCircle data-sentry-element="MinusCircle" data-sentry-source-file="temp-product-modal.tsx" />
							</IconButton>
							<Chip label={tempProductInput?.quantity ?? 0} variant="filled" data-sentry-element="Chip" data-sentry-source-file="temp-product-modal.tsx" />
							<IconButton color="inherit" onClick={onIncreaseItemQuantity} data-sentry-element="IconButton" data-sentry-source-file="temp-product-modal.tsx">
								<PlusCircle data-sentry-element="PlusCircle" data-sentry-source-file="temp-product-modal.tsx" />
							</IconButton>
						</Stack>
						<Button sx={{
            ml: 2
          }} variant="contained" onClick={handleClose} data-sentry-element="Button" data-sentry-source-file="temp-product-modal.tsx">
							Done
						</Button>
					</Toolbar>
				</AppBar>
				<Grid container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="temp-product-modal.tsx">
					{!!selectedProduct && selectedProduct._options.map(option => {
          const addedOption = tempProductInput?.options?.find(o => o.option === option._id);
          return <Grid key={option._id} xs={6} md={4}>
									<Card variant="outlined" sx={[{
              cursor: "pointer",
              ":hover": {
                boxShadow: t => t.shadows[8]
              }
            }, !!addedOption && {
              boxShadow: t => t.shadows[8],
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: t => t.palette.primary.contrastText,
              ".MuiCardHeader-subheader": {
                color: t => t.palette.primary.contrastText
              }
            }]} onClick={() => onOptionClick(option)}>
										<CardHeader title={option.name} subheader={getOptionPrice(option)} action={addedOption ? <Stack direction="row" alignItems="center">
														<IconButton color="inherit" onClick={e => {
                  e.stopPropagation();
                  onDecreaseOptionQuantity(addedOption._id);
                }}>
															<MinusCircle />
														</IconButton>
														<Chip label={addedOption.quantity} variant="filled" sx={{
                  color: t => t.palette.primary.contrastText
                }} />
														<IconButton color="inherit" onClick={e => {
                  e.stopPropagation();
                  onIncreaseOptionQuantity(addedOption._id);
                }}>
															<PlusCircle />
														</IconButton>
													</Stack> : null} />
										<CardContent />
										<CardActions />
									</Card>
								</Grid>;
        })}

					{!!selectedProduct && !selectedProduct._options.length && !selectedProduct.configuredOptionGroups.length && <Grid xs={12} width="100%" textAlign="center">
								<Box alt="Not found" component="img" src="/assets/errors/error-404.png" sx={{
            height: "auto",
            maxWidth: "100%",
            width: "200px",
            mx: "auto",
            mb: 2
          }} />
								<Typography variant="h6">
									This product has no options
								</Typography>
							</Grid>}
				</Grid>
				<Stack alignSelf="flex-end" width="100%" direction="row" justifyContent="right" alignItems="center" paddingRight={2} gap={1} data-sentry-element="Stack" data-sentry-source-file="temp-product-modal.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="temp-product-modal.tsx">Fix Discount</Typography>
					<TextField key={tmpDiscount.value} type="number" defaultValue={tmpDiscount.value} sx={{
          width: "150px",
          "& .MuiOutlinedInput-root": {
            pl: 0
          },
          "& input": {
            textAlign: "right"
          }
        }} InputProps={{
          startAdornment: <IconButton color="primary" onClick={handleDiscountTypeChanged}>
									<Typography variant="body1">
										{tmpDiscount.type === DISCOUNT_TYPE.AMOUNT ? "$" : "%"}
									</Typography>
								</IconButton>
        }} onFocus={e => {
          if (Number(e.target.value) === 0) e.target.value = "";
        }} onBlur={e => handleDiscountValueChanged(e.target.value)} data-sentry-element="TextField" data-sentry-source-file="temp-product-modal.tsx" />
				</Stack>
				<Stack alignSelf="flex-end" width="100%" direction="row" justifyContent="right" alignItems="center" paddingRight={2} mt={2} gap={1} data-sentry-element="Stack" data-sentry-source-file="temp-product-modal.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="temp-product-modal.tsx">Additional Fee</Typography>
					<TextField key={tmpFee.value} type="number" defaultValue={tmpFee.value} sx={{
          width: "150px",
          "& .MuiOutlinedInput-root": {
            pl: 0
          },
          "& input": {
            textAlign: "right"
          }
        }} InputProps={{
          startAdornment: <IconButton color="primary" onClick={handleFeeTypeChanged}>
									<Typography variant="body1">
										{tmpFee.type === DISCOUNT_TYPE.AMOUNT ? "$" : "%"}
									</Typography>
								</IconButton>
        }} onFocus={e => {
          if (Number(e.target.value) === 0) e.target.value = "";
        }} onBlur={e => handleFeeValueChanged(e.target.value)} data-sentry-element="TextField" data-sentry-source-file="temp-product-modal.tsx" />
				</Stack>
			</Box>
		</Dialog>;
};